// recap.tsx — an OPTIONAL, off-by-default periodic "moment to remember" nudge.
//
// It answers Forever Paw's repeat-use weakness WITHOUT any new AI: the recap itself is the
// EXISTING "their story" feature (the RemembranceCard / weaveRemembrance call). This module only
// adds a gentle recurring reminder that leads the customer back to it, on a cadence they choose.
//
// FENCE (this is a bereavement-aware app — handle with care): the nudge is warm and quiet, never
// intrusive, never salesy, never a streak or a target, and it NEVER implies the pet is present or
// fabricates anything — it points to the moments the customer has SAVED themselves. Off by default;
// one tap to turn off. Scheduling reuses the shared reminders.ts (no new call shape, no new dep).
import { useState } from "react";
import { platform } from "./tauri-web";
import type { Reminder } from "./reminders";

const KEY = "oriora.foreverpaw.recap";
export type RecapCadence = "monthly" | "seasonal";
export interface RecapSetting {
  enabled: boolean;
  cadence: RecapCadence;
}
const DEFAULT: RecapSetting = { enabled: false, cadence: "monthly" };

export function loadRecap(): RecapSetting {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT };
    const v = JSON.parse(raw) as Partial<RecapSetting>;
    const cadence: RecapCadence = v.cadence === "seasonal" ? "seasonal" : "monthly";
    return { enabled: !!v.enabled, cadence };
  } catch {
    return { ...DEFAULT };
  }
}
export function saveRecap(s: RecapSetting): void {
  try { localStorage.setItem(KEY, JSON.stringify(s)); } catch { /* private mode — just won't persist */ }
}

/**
 * The next few recap nudges (empty when off), to be MERGED into the app's single syncReminders
 * call alongside the care-due reminders. Monthly = every ~1 month, seasonal = every ~3 months, at
 * 09:00 local; the first is one full period out (a recap is periodic, never immediate). Re-laid on
 * every app open, so the schedule stays filled; syncReminders replaces by key and drops past ones.
 */
export function recapReminders(petName: string, setting: RecapSetting): Reminder[] {
  if (!setting.enabled) return [];
  const stepMonths = setting.cadence === "seasonal" ? 3 : 1;
  const who = petName?.trim() || "your companion";
  const now = new Date();
  const out: Reminder[] = [];
  for (let i = 1; i <= 3; i++) {
    const at = new Date(now.getFullYear(), now.getMonth() + stepMonths * i, now.getDate(), 9, 0, 0, 0);
    if (Number.isNaN(at.getTime())) continue;
    out.push({
      key: `recap-${at.toISOString().slice(0, 10)}`,
      title: "A moment to remember",
      body: `Look back on the moments you've saved with ${who}.`,
      at,
    });
  }
  return out;
}

/** Off-by-default toggle + cadence. Hidden on web (no notification plugin — never a dead switch). */
export function RecapControl({ setting, onChange }: { setting: RecapSetting; onChange: (s: RecapSetting) => void }) {
  const [s, setS] = useState<RecapSetting>(setting);
  if (platform() === "web") return null;
  const apply = (next: RecapSetting) => { setS(next); onChange(next); };
  return (
    <div className="row" style={{ gap: "0.6rem", alignItems: "center", flexWrap: "wrap", marginTop: "0.75rem" }}>
      <label className="row" style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
        <input
          type="checkbox"
          checked={s.enabled}
          onChange={(e) => apply({ ...s, enabled: e.target.checked })}
          aria-label="A gentle recurring moment to look back"
        />
        A gentle moment to look back, now and then
      </label>
      {s.enabled && (
        <select
          value={s.cadence}
          aria-label="How often"
          onChange={(e) => apply({ ...s, cadence: e.target.value === "seasonal" ? "seasonal" : "monthly" })}
          style={{ fontSize: "var(--fs-sm)" }}
        >
          <option value="monthly">monthly</option>
          <option value="seasonal">seasonally</option>
        </select>
      )}
    </div>
  );
}
